/**
 * Planner blueprint (bp) vertical-rail chrome wiring.
 *
 * The planner engine (state.ts, render.ts, events.ts, canvas.ts) is
 * imported verbatim by the `planner` Vite entrypoint — this module
 * wires up the pv2 shell:
 *
 *   1. Rail buttons — engine already binds to `#btnSelect` / `data-mode`.
 *      We add the `on` active state and flip the matching flyout page.
 *   2. Flyout tabs (zones / layers / props).
 *   3. Flyout page switching per tool (`data-pv2-page` matches `data-pv2-tool`).
 *   4. Context bar title + tool-specific section visibility.
 *   5. Layer toggles for the engine's SVG <g> groups.
 *   6. Status bar observers (heads / flow / pressure / CU / pipe).
 *   7. Crosshair cursor HUD mirrored from `#statusCoords`.
 *   8. Zoom chip mirrored from `#zoomLevel`.
 *   9. Feature-flag visibility (subsystem flags via data-v2-flag).
 *  10. Console log mirroring engine toasts.
 *  11. Export / share actions delegating to the engine buttons.
 *  12. Title-bar actions (save, export, history).
 *
 * We intentionally do NOT duplicate event handlers the engine already
 * binds in planner/app.ts — we merely observe and reflect.
 */
import { featureEnabled } from '../shared/features.ts'
import { applyTranslations, t } from '../shared/i18n.ts'
import { generateHydraulicReport } from '../calculations/logic.ts'
import { renderGrid } from '../planner/render.ts'
import { state as plannerState, getCurrentProjectId, getCurrentProjectName } from '../planner/state.ts'
import type { create3DView } from '../planner/three-view.ts'
import { authenticatedFetch, isEditableElement, showToast } from '../shared/utils.ts'
import { trackProjectCreated, trackProjectShared } from '../shared/analytics.ts'
import { formatLength, formatPressure, formatRadius, formatSmallLength, getUnitLabels } from '../shared/units.ts'
import { isAuthenticated } from '../shared/auth.ts'
import { api_v1_project_path, api_v1_projects_path } from '../shared/api-routes.ts'

type LayerId =
  | 'grid'
  | 'zones'
  | 'pipes'
  | 'drip'
  | 'trees'
  | 'valves'
  | 'weather'
  | 'labels'

// `coverage` is no longer in this map: its visibility is driven by the
// dedicated `#pl2-layer-coverage-view` select (rings | heatmap | off)
// in app.ts, not a checkbox + display toggle. The corresponding SVG
// `<g>` groups (#coverageLayer, #heatmapLayer) stay visible at all
// times and the render functions internally bail out based on
// `state.coverageView`. This unifies the two duplicate checkboxes that
// used to live here pre-#1204.
//
// `grid` is in the map but receives a special-case branch in
// `bindLayerToggles` (#1205): the checkbox both flips
// `plannerState.showGrid` AND re-runs `renderGrid()` so the gridlines
// reappear, mirroring the coverage-view pattern from #1204. Display-
// toggle alone is insufficient because `renderGrid()` clears
// `<g id="gridLayer">` whenever `state.showGrid === false`, so
// re-checking the box would leave an empty layer until some other
// mutation triggered the next full render. `renderGrid()` is the only
// render path that observes `state.showGrid`, so we call it directly
// instead of paying for a full `renderAll()` cascade.
const LAYER_TARGETS: Record<LayerId, string> = {
  grid: '#gridLayer',
  zones: '#zoneLayer',
  pipes: '#pipeLayer',
  drip: '#dripLayer',
  trees: '#treeLayer',
  valves: '#valveObjLayer',
  weather: '#weatherStationLayer',
  labels: '#labelLayer',
}

/* Mapping: rail tool key -> engine mode (data-mode). Tools that have no
 * direct engine counterpart (pan/hydr/sched/inspect) leave the mode
 * alone and just swap the flyout. */
const TOOL_TO_MODE: Record<string, string | null> = {
  select: 'select',
  pan: null,
  draw: 'boundary',
  zones: 'zone',
  heads: 'sprinkler',
  pipes: 'pipe',
  drip: 'drip',
  valves: 'valveObj',
  weather: 'weatherStation',
  hydr: null,
  sched: null,
  inspect: 'tree',
}

/* Mapping: rail tool -> flyout "page" (data-pv2-page) to reveal.
 * Default tabs (zones/layers/props) map to their own pages. */
const TOOL_TO_PAGE: Record<string, string> = {
  select: 'zones',
  pan: 'pan',
  draw: 'draw',
  zones: 'zones',
  heads: 'heads',
  pipes: 'pipes',
  drip: 'drip',
  valves: 'valves',
  weather: 'weather',
  hydr: 'hydr',
  sched: 'sched',
  inspect: 'inspect',
}

/* Context-bar section visibility per tool. Keys are the section IDs
 * in the ctx-left group; value `true` means show, `false` / absent
 * means hide. */
const TOOL_CTX_SECTIONS: Record<string, string[]> = {
  heads: ['sprinklerToolbarGroup'],
  pipes: ['pipeToolbarGroup'],
  drip: ['dripToolbarGroup'],
  inspect: ['objectToolbarGroup'],
}

// The status poll interval is module-scoped so we can (a) guarantee
// `initPlannerBpChrome()` is idempotent across hot-reloads and Turbo
// navigations, and (b) actually clear the timer on pagehide instead of
// letting it pile up on every re-mount.
let statusPollHandle: ReturnType<typeof setInterval> | null = null
let hydraulicsPollHandle: ReturnType<typeof setInterval> | null = null

export function initPlannerBpChrome(): void {
  if (statusPollHandle !== null) return

  bindRailTools()
  bindEngineModeSync()
  bindFlyoutTabs()
  bindLayerToggles()
  bindSelectionRevealsPanel()
  bindStatusObservers()
  bindHydraulicsPanel()
  bindCrosshair()
  bindZoomChip()
  bindFeatureFlagVisibility()
  bindExportAndShare()
  bindInspectorToggle()
  bindViewToggle()
  bindProjectSwitcher()
  bindRailShortcuts()

  // Prime the flyout + context bar to match whatever tool the engine
  // starts with (defaults to "select").
  activateTool('select')

  // Re-run i18n after chrome mount so aria-label/title attributes on SSR
  // chrome nodes (title bar, context bar, zoom controls) are localised.
  applyTranslations()
}

/* Rail buttons drive three things at once: the engine mode (via the
 * engine's own click binding on `.tool-btn[data-mode]`), the rail
 * visual state (.on + red edge), and the flyout/context switch. */
function bindRailTools(): void {
  document.querySelectorAll<HTMLButtonElement>('.pv2-rail-btn[data-pv2-tool]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tool = btn.dataset.pv2Tool
      if (!tool) return
      activateTool(tool)
    })
  })
}

/* The rail prints a key beside each tool (1–0) and ⌘E on the PDF export, and
 * the shortcut help lists G for the grid, but the engine's handler in
 * events.ts only knows the letter shortcuts, so none of these keys did
 * anything. A digit presses the rail button whose `.kbd` hint shows it — the
 * markup stays the one list of which key is which tool. Ctrl/⌘ + digit is left
 * to the browser's tab switching, and keys typed into a field or a dialog are
 * not shortcuts. */
function bindRailShortcuts(): void {
  const byDigit = new Map<string, HTMLButtonElement>()
  document.querySelectorAll<HTMLButtonElement>('.pv2-rail-btn[data-pv2-tool]').forEach((btn) => {
    const key = btn.querySelector('.kbd')?.textContent?.trim() ?? ''
    if (/^\d$/.test(key)) byDigit.set(key, btn)
  })
  const exportPdf = document.getElementById('btnExportPDF') as HTMLButtonElement | null
  const grid = document.getElementById('pl2-layer-grid') as HTMLInputElement | null

  document.addEventListener('keydown', (e) => {
    if (e.altKey || isEditableElement(e.target)) return
    if (e.target instanceof Element && e.target.closest('[role="dialog"]')) return
    const key = e.key.toLowerCase()

    if (e.ctrlKey || e.metaKey) {
      if (key !== 'e' || e.shiftKey || !exportPdf || exportPdf.hidden || exportPdf.disabled) return
      e.preventDefault()
      exportPdf.click()
      return
    }

    const target = key === 'g' ? grid : byDigit.get(key)
    if (!target || target.hidden) return
    e.preventDefault()
    target.click()
  })
}

/* Keyboard shortcuts (P, B, S, Esc, …) switch the engine through its own
 * setMode(), which never reached this module: the rail kept the old tool lit
 * beside the new one and the flyout kept showing the old tool's panel.
 * setMode() records the mode on #canvasContainer[data-mode], so follow that.
 * Only real changes count — Esc re-asserts "select", and that must not yank
 * the user off whatever flyout tab they had open. */
function bindEngineModeSync(): void {
  const canvas = document.getElementById('canvasContainer')
  if (!canvas) return
  const toolForMode = new Map(
    Object.entries(TOOL_TO_MODE)
      .filter((entry): entry is [string, string] => entry[1] !== null)
      .map(([tool, mode]) => [mode, tool]),
  )
  new MutationObserver((records) => {
    const mode = canvas.dataset.mode
    if (!mode || records.every((r) => r.oldValue === mode)) return
    const tool = toolForMode.get(mode)
    if (tool) activateTool(tool)
  }).observe(canvas, { attributes: true, attributeFilter: ['data-mode'], attributeOldValue: true })
}

/* Reveal a flyout page without touching the active tool. Selecting a
 * sprinkler has to show the sprinkler panel, but it must NOT flip the rail
 * from Select into Heads — that would change what the next canvas click
 * draws. */
function showFlyoutPage(page: string): void {
  document.querySelectorAll<HTMLElement>('.pv2-fly-page').forEach((el) => {
    el.classList.toggle('on', el.dataset.pv2Page === page)
  })

  // Flyout default tabs (zones/layers/props) — keep the top-level tab
  // highlighted if the active page matches one of them.
  document.querySelectorAll<HTMLButtonElement>('.pv2-fly-tabs button').forEach((b) => {
    const tab = b.dataset.pv2Tab
    const selected = tab === page || (tab === 'zones' && !['layers', 'props'].includes(page))
    b.classList.toggle('on', selected)
    b.setAttribute('aria-selected', String(selected))
  })
}

function activateTool(tool: string): void {
  // 1. Rail button state — visual, and aria-pressed for screen readers
  //    (the markup only ever marked Select as pressed).
  document.querySelectorAll<HTMLButtonElement>('.pv2-rail-btn[data-pv2-tool]').forEach((b) => {
    const isActive = b.dataset.pv2Tool === tool
    b.classList.toggle('on', isActive)
    b.classList.toggle('active', isActive)
    b.setAttribute('aria-pressed', String(isActive))
  })

  // 2 + 3. Flyout page and tab highlight
  const page = TOOL_TO_PAGE[tool] || 'zones'
  showFlyoutPage(page)

  // 4. Context bar title + sections. The tool name used to be the English
  //    key, capitalised, so every other locale read "Heads" after the first
  //    click. t() still hands back the key before the locale arrives.
  const toolKey = `plannerBp.tool.${tool}`
  const translated = t(toolKey)
  const toolName = translated === toolKey ? tool.charAt(0).toUpperCase() + tool.slice(1) : translated
  const titleEl = document.querySelector<HTMLElement>('[data-pv2-ctx-title]')
  if (titleEl) {
    titleEl.dataset.i18n = toolKey
    titleEl.textContent = toolName
  }

  const toolSections = TOOL_CTX_SECTIONS[tool] || []
  ;['sprinklerToolbarGroup', 'pipeToolbarGroup', 'dripToolbarGroup', 'objectToolbarGroup'].forEach((id) => {
    const el = document.getElementById(id)
    if (!el) return
    if (toolSections.includes(id)) el.removeAttribute('hidden')
    else el.setAttribute('hidden', '')
  })

  // 5. Status-bar tool label — rebuilt as the markup has it, a data-i18n span,
  //    so a later language switch re-translates it too.
  const toolLabel = document.querySelector<HTMLElement>('[data-pv2-tool-label]')
  if (toolLabel) {
    const labelText = document.createElement('span')
    labelText.dataset.i18n = toolKey
    labelText.textContent = toolName.toUpperCase()
    toolLabel.replaceChildren(labelText)
  }

  // 6. Delegate to the engine if this tool maps to a mode. We click
  //    the existing engine button so its handler runs (keeps parity
  //    with shortcut keys, autosave, etc.).
  const mode = TOOL_TO_MODE[tool]
  if (mode) {
    const engineBtn = document.querySelector<HTMLButtonElement>(`.tool-btn[data-mode="${mode}"]`)
    if (engineBtn && !engineBtn.classList.contains('active')) {
      // Avoid recursion — the engine calls our activateTool chain indirectly
      // through the rail button class we already added, but this click is
      // what tells the engine to change `state.mode`.
      engineBtn.click()
    }
  }
}

/* Top-level flyout tabs (zones / layers / props). */
function bindFlyoutTabs(): void {
  document.querySelectorAll<HTMLButtonElement>('.pv2-fly-tabs button').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.pv2Tab
      if (!tab) return
      document.querySelectorAll<HTMLButtonElement>('.pv2-fly-tabs button').forEach((b) => {
        b.classList.toggle('on', b === btn)
        b.setAttribute('aria-selected', b === btn ? 'true' : 'false')
      })
      document.querySelectorAll<HTMLElement>('.pv2-fly-page').forEach((el) => {
        el.classList.toggle('on', el.dataset.pv2Page === tab)
      })
    })
  })
}

/* Layer toggles map to the engine's SVG <g> groups.
 *
 * The Coverage layer is intentionally absent from this loop — it is
 * driven by `#pl2-layer-coverage-view` (a select with off/rings/heatmap
 * options) wired up in planner/app.ts. Pre-#1204 the heatmap entry
 * needed a special-case bridge to a duplicate `#showHeatmap` checkbox
 * in the context bar (sub-5 of #1118); that checkbox no longer exists,
 * so the case goes away.
 *
 * The Grid layer needs a special-case in #1205: `renderGrid()` empties
 * `<g id="gridLayer">` whenever `state.showGrid === false`, so a plain
 * display-toggle would leave the layer empty after a hide/show cycle.
 * We mirror the unified coverage approach: write to `state.showGrid`
 * and re-run `renderGrid()` so the engine re-emits the gridlines.
 * `renderGrid()` is the only render path that observes
 * `state.showGrid`, so a targeted re-render is enough — no need to pay
 * for a full `renderAll()` cascade. The remaining layers (zones /
 * pipes / drip / trees / valves / weather / labels) keep the cheap
 * CSS display toggle since their render paths don't depend on a state
 * flag. */
function bindLayerToggles(): void {
  Object.entries(LAYER_TARGETS).forEach(([id, selector]) => {
    const cb = document.getElementById(`pl2-layer-${id}`) as HTMLInputElement | null
    if (!cb) return

    if (id === 'grid') {
      cb.addEventListener('change', () => {
        plannerState.showGrid = cb.checked
        renderGrid()
      })
      // Prime state from the (default-checked) DOM in case the engine's
      // initial render runs before the user touches the box.
      plannerState.showGrid = cb.checked
      return
    }

    const apply = () => {
      const node = document.querySelector<SVGGElement>(selector)
      if (node) node.classList.toggle('pv2-layer-off', !cb.checked)
    }
    cb.addEventListener('change', apply)
    apply()
  })
}

/* Hydraulics flyout — inlet pressure, head spacing, pipe spec, friction loss.
 *
 * These five readouts shipped as literal text in index.html.erb ("3.2 bar",
 * "25 mm PE", "150 (HDPE)", a bare em dash for loss) and nothing ever wrote
 * to them, so the panel showed the same numbers for an empty project and for
 * a fully piped one. For an irrigation tool that is worse than showing
 * nothing: the figures look like a calculation of the user's own system.
 *
 * The maths already existed in calculations/logic.ts — only the wiring was
 * missing. */
/** Write to every match. The hydraulics block is rendered in both the
 *  `props` and `hydr` flyout pages, so the single-element `setText` would
 *  silently update whichever copy happens to come first in the document —
 *  which is not the one the HYDR rail button opens. */
function setAllText(selector: string, text: string): void {
  document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
    el.textContent = text
  })
}

const ROUGHNESS_C: Record<string, number> = { PE: 150, HDPE: 150, PVC: 150, PEX: 150, COPPER: 130, STEEL: 120 }

function readSliderValue(id: string, fallback: number): number {
  const raw = (document.getElementById(id) as HTMLInputElement | null)?.value
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

/** Distance to the nearest other head, averaged — what "head spacing" means
 *  on a plan. Falls back to the slider while fewer than two heads exist. */
function measuredHeadSpacing(sprinklers: any[]): number | null {
  if (!Array.isArray(sprinklers) || sprinklers.length < 2) return null
  let total = 0
  for (const a of sprinklers) {
    let nearest = Infinity
    for (const b of sprinklers) {
      if (a === b) continue
      const d = Math.hypot(b.x - a.x, b.y - a.y)
      if (d < nearest) nearest = d
    }
    total += nearest
  }
  return total / sprinklers.length
}

function bindHydraulicsPanel(): void {
  const update = () => {
    const state = (window as any).__smartpluvia?.getState?.()
    if (!state) return

    const sprinklers = state.sprinklers ?? []
    const zones = state.zones ?? []
    const pipes = state.pipes ?? []
    const units = state.userUnits

    const sourcePressure = readSliderValue('pl2-slider-pressure', 3.2)
    setAllText('[data-hyd-pressure]', formatPressure(sourcePressure, units))

    const spacing = measuredHeadSpacing(sprinklers) ?? readSliderValue('pl2-slider-spacing', 3.5)
    setAllText('[data-hyd-spacing]', formatRadius(spacing, units))

    // Report every distinct diameter actually drawn rather than a fixed
    // default, so changing a pipe is visible here.
    const diameters = [...new Set<number>(pipes.map((p: any) => Number(p.diameter) || 25))].sort((a, b) => a - b)
    const material = String(pipes[0]?.material ?? 'PE').toUpperCase()
    const sizes = diameters.map((d) => formatSmallLength(d, units)).join(' / ')
    setAllText('[data-hyd-dia]', diameters.length ? `${sizes} ${material}` : '—')
    setAllText('[data-hyd-roughness]', diameters.length ? `${ROUGHNESS_C[material] ?? 150} (${material})` : '—')

    const lossEls = [...document.querySelectorAll<HTMLElement>('[data-hyd-loss]')]
    if (!lossEls.length) return

    if (!zones.length || !pipes.length) {
      lossEls.forEach((el) => {
        el.textContent = '—'
        el.classList.remove('pl-loss-warn')
        el.classList.add('pl-loss-ok')
        el.removeAttribute('title')
      })
      return
    }

    const report = generateHydraulicReport({ sprinklers, zones, pipes, sourcePressure })
    // Worst zone drives the headline: a system is only as good as the zone
    // that loses the most.
    const worst = report.zones.reduce(
      (acc: any, z: any) => (acc === null || z.pressure.frictionLoss > acc.pressure.frictionLoss ? z : acc),
      null,
    )
    if (!worst) return

    const loss = worst.pressure.frictionLoss
    const short = !worst.pressure.sufficient
    const available = formatPressure(worst.pressure.available, units)
    const detail = short
      ? t('plannerBp.hydraulics.pressureShort', {
          zone: worst.zoneName, available, required: formatPressure(worst.pressure.required, units),
        })
      : t('plannerBp.hydraulics.pressureAvailable', { zone: worst.zoneName, available })

    lossEls.forEach((el) => {
      el.textContent = formatPressure(loss, units)
      el.classList.toggle('pl-loss-warn', short)
      el.classList.toggle('pl-loss-ok', !short)
      el.title = detail
    })
  }

  update()
  hydraulicsPollHandle = setInterval(update, 600)
  document.getElementById('pl2-slider-pressure')?.addEventListener('input', update)
  document.getElementById('pl2-slider-spacing')?.addEventListener('input', update)

  window.addEventListener('pagehide', () => {
    if (hydraulicsPollHandle !== null) {
      clearInterval(hydraulicsPollHandle)
      hydraulicsPollHandle = null
    }
  })
}

/* Status bar (heads / flow / pressure / CU / pipe / cost). */
/* Counts beside each row of the Layers flyout. The markup shipped them as
 * literal `0`s and nothing ever wrote to them, so a plan with 19 heads and a
 * zone still read zero across the board (#1599). Coverage and Labels count
 * sprinklers because both are drawn one-per-head. */
const LAYER_COUNT_SOURCES: Record<string, string> = {
  layerCountCoverage: 'sprinklers',
  layerCountLabels: 'sprinklers',
  layerCountZones: 'zones',
  layerCountPipes: 'pipes',
  layerCountDrip: 'dripLines',
  layerCountTrees: 'trees',
  layerCountValves: 'valveObjects',
  layerCountWeather: 'weatherStations',
}

function updateLayerCounts(state: Record<string, unknown>): void {
  for (const [id, key] of Object.entries(LAYER_COUNT_SOURCES)) {
    const el = document.getElementById(id)
    if (!el) continue
    const collection = state[key]
    const count = Array.isArray(collection) ? collection.length : 0
    const next = String(count)
    if (el.textContent !== next) el.textContent = next
  }
}

/* Which flyout page holds each element's properties. The v1 sidebar showed
 * every panel at once, so selecting an object was enough to see its fields.
 * The blueprint chrome split them across pages and the link was never
 * rebuilt, so clicking a sprinkler highlighted it and showed nothing — its
 * panel sat on the Heads page, 0×0, behind a tab you had to know about. */
const SELECTION_TO_PAGE: Array<[string, string]> = [
  ['selectedSprinkler', 'heads'],
  ['selectedZone', 'zones'],
  ['selectedPipe', 'pipes'],
  ['selectedDripLine', 'drip'],
  ['selectedValveObj', 'valves'],
  ['selectedWeatherStation', 'weather'],
  ['selectedTree', 'inspect'],
]

function bindSelectionRevealsPanel(): void {
  let last = ''

  const sync = () => {
    const state = (window as any).__smartpluvia?.getState?.()
    if (!state) return

    const hit = SELECTION_TO_PAGE.find(([key]) => state[key] !== null && state[key] !== undefined)
    // Only act on a *change*: re-asserting the page on every tick would
    // fight the user the moment they open Layers while something is
    // selected.
    const signature = hit ? `${hit[0]}:${state[hit[0]]}` : ''
    if (signature === last) return
    last = signature
    if (!hit) return
    showFlyoutPage(hit[1])
    // A row picked with the keyboard can sit in a panel the new selection
    // hides (a zone's own sprinkler list), which drops focus onto <body>.
    // Hand it to the selected row on the page just revealed.
    const active = document.activeElement
    if (!active || active === document.body || active.closest('[hidden]')) {
      document.querySelector<HTMLElement>('.pv2-fly-page.on .list-item-select[aria-current="true"]')?.focus()
    }
  }

  const canvas = document.getElementById('planner-canvas') ?? document
  // `mouseup` rather than `click`: a drag that ends on an element still
  // changes the selection, and the engine has written state by then.
  canvas.addEventListener('mouseup', () => setTimeout(sync, 0))
  // Enter and Space: selecting from a flyout list with the keyboard.
  document.addEventListener('keyup', (e) => {
    if (['Escape', 'Enter', ' '].includes((e as KeyboardEvent).key)) setTimeout(sync, 0)
  })
}

function bindStatusObservers(): void {
  const update = () => {
    const sp = (window as any).__smartpluvia
    const state = sp?.getState?.()
    if (!state) return

    const heads = state.sprinklers?.length ?? 0
    setText('[data-stat-heads]', String(heads))

    // Mirror the engine's already-computed `#statFlow` rather than
    // re-summing `s.flow` from raw state — sprinkler flow is derived
    // from the catalog at render time (`getArcData(model, arc).flow`)
    // and is not stored on the sprinkler record, so the local sum was
    // always 0.0 even when the sidebar showed the right number.
    const flowText = document.getElementById('statFlow')?.textContent?.trim()
    if (flowText) setText('[data-stat-flow]', flowText)

    const pipeLen = (state.pipes ?? []).reduce((acc: number, p: any) => {
      if (!Array.isArray(p.points)) return acc
      let len = 0
      for (let i = 1; i < p.points.length; i++) {
        const a = p.points[i - 1]
        const b = p.points[i]
        len += Math.hypot(b.x - a.x, b.y - a.y)
      }
      return acc + len
    }, 0)
    // Route through the shared formatters like the rest of the planner —
    // the status bar used to hardcode metric and ignored state.userUnits.
    setText('[data-stat-pipe]', formatLength(pipeLen, state.userUnits))

    const pressure = Number(
      (document.getElementById('pl2-slider-pressure') as HTMLInputElement | null)?.value ?? 3.2,
    )
    setText('[data-stat-pressure]', formatPressure(pressure, state.userUnits))

    const cov = document.getElementById('statCoverage')?.textContent
    if (cov) setText('[data-stat-cu]', cov)

    // Project summary in the status bar. Only the plot area: nothing in the
    // project records a soil type or an ETo, and the "loam · ETo 4.2 mm/d"
    // this used to append was the same constant on every plan.
    const areaEl = document.getElementById('statArea')
    const areaText = areaEl?.textContent?.trim()
    const summaryEl = document.querySelector<HTMLElement>('[data-project-summary]')
    if (summaryEl && areaText) {
      summaryEl.textContent = areaText
    }

    updateLayerCounts(state)
  }

  // Engine re-renders synchronously on each mutation; polling at a modest
  // interval keeps the status bar fresh without custom event plumbing.
  update()
  statusPollHandle = setInterval(update, 600)
  document.addEventListener('visibilitychange', update)

  window.addEventListener('pagehide', () => {
    if (statusPollHandle !== null) {
      clearInterval(statusPollHandle)
      statusPollHandle = null
    }
  })
}

/* Crosshair — reshape the engine's cursor coord string for the CAD look. */
function bindCrosshair(): void {
  const readout = document.querySelector<HTMLElement>('[data-cursor-readout]')
  const coords = document.getElementById('statusCoords')
  if (!readout || !coords) return
  const observer = new MutationObserver(() => {
    const text = coords.textContent || ''
    // `\s*` before the colon: French typography writes "X : 3.2 m".
    const match = /X\s*:\s*([-\d.]+).*Y\s*:\s*([-\d.]+)/.exec(text)
    if (match) readout.textContent = `X ${match[1]} \u00b7 Y ${match[2]} ${getUnitLabels(plannerState.userUnits).length}`
  })
  observer.observe(coords, { childList: true, characterData: true, subtree: true })
}

/* Zoom chip mirrors the engine's #zoomLevel readout. */
function bindZoomChip(): void {
  // We now share the DOM node with the engine (it's inside `.zoom-controls`),
  // so no mirroring is needed — keep the observer as a no-op stub for any
  // future external zoom chip.
  const chip = document.querySelector<HTMLElement>('[data-zoom-value]')
  const zoom = document.getElementById('zoomLevel')
  if (!chip || !zoom) return
  const observer = new MutationObserver(() => {
    chip.textContent = (zoom.textContent || '100%').trim()
  })
  observer.observe(zoom, { childList: true, characterData: true, subtree: true })
}

/* Feature flag visibility — hides any element with a data-v2-flag whose flag is disabled. */
function bindFeatureFlagVisibility(): void {
  document.querySelectorAll<HTMLElement>('[data-v2-flag]').forEach((el) => {
    const name = el.dataset.v2Flag
    if (!name) return
    if (!featureEnabled(name)) {
      el.hidden = true
      el.setAttribute('aria-hidden', 'true')
    }
  })
}

/* Title-bar export/share buttons delegate to the engine's UI. */
function bindExportAndShare(): void {
  document.querySelector<HTMLButtonElement>('[data-v2-action="export-pdf"]')?.addEventListener('click', () => {
    document.getElementById('btnExportPDF')?.click()
  })
  document.querySelector<HTMLButtonElement>('[data-v2-action="export-svg"]')?.addEventListener('click', () => {
    document.getElementById('btnExportSVG')?.click()
  })
  document.querySelector<HTMLButtonElement>('[data-v2-action="share"]')?.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      trackProjectShared()
    } catch {
      /* swallow — clipboard may be blocked in iframes */
    }
  })
  document.querySelector<HTMLButtonElement>('[data-v2-action="save"]')?.addEventListener('click', () => {
    // Engine autosaves on every mutation — trigger a flash on the save pill
    const dot = document.querySelector<HTMLElement>('.pv2-title .sv')
    if (dot) {
      dot.classList.add('pulse')
      setTimeout(() => dot.classList.remove('pulse'), 1400)
    }
  })
  document.querySelector<HTMLButtonElement>('[data-v2-action="history"]')?.addEventListener('click', () => {
    document.getElementById('btnUndo')?.click()
  })
}

/* Inspector toggle button in the top-right ctx bar. This currently
 * hides/shows the entire right-hand group but the engine's own
 * selection-driven panels live inside the flyout now, so this is a
 * no-op placeholder for a future floating inspector. */
// PROPERTIES toggle in the context-bar — collapse / expand the flyout
// sidebar (`.pv2-fly`) so the user can free up canvas width when the
// inspector isn't needed. Pre-#1329 the handler only flipped its own
// aria-pressed state — no UI change actually happened, which read as
// "the PROPERTIES button не реагує на клік".
function bindInspectorToggle(): void {
  const btn = document.querySelector<HTMLButtonElement>('[data-v2-action="toggle-inspector"]')
  if (!btn) return
  const flyout = document.querySelector<HTMLElement>('.pv2-fly')
  btn.addEventListener('click', () => {
    const pressed = btn.getAttribute('aria-pressed') === 'true'
    btn.setAttribute('aria-pressed', pressed ? 'false' : 'true')
    btn.classList.toggle('on', !pressed)
    // pressed=true → was open, now collapse. pressed=false → reopen.
    flyout?.classList.toggle('pv2-fly-collapsed', pressed)
  })
}

function setText(selector: string, text: string): void {
  const el = document.querySelector<HTMLElement>(selector)
  if (el) el.textContent = text
}

/* 2D / 3D view toggle in the top-right ctx bar. Switches between the
 * SVG-driven planner canvas (default) and a Three.js scene rendered into
 * `#three-container`. The state container's `.three-mode` class hides
 * the SVG via the existing `three-view.css` rule.
 *
 * The 3D view is built lazily on first activation, then disposed when
 * the user switches back to 2D — there's no point holding a WebGL
 * context + animation loop while the user works in 2D.
 *
 * create3DView() runs its own requestAnimationFrame loop and dispose()
 * cancels it. This module used to start a second loop calling
 * view.update() on top, which rendered the scene twice per frame.
 */
let threeView: ReturnType<typeof create3DView> | null = null

async function activate3D(): Promise<void> {
  const container = document.getElementById('three-container')
  const canvasContainer = document.getElementById('canvasContainer')
  if (!container || !canvasContainer) return

  // Loaded on demand rather than imported at the top of the file. Three.js is
  // ~550 KB and it was landing in the main planner chunk, so every planner
  // visitor downloaded, parsed and executed it — including the ones for whom
  // the `three_d_view` flag is off, since the flag only hid the button.
  const { create3DView, renderProject3D } = await import('../planner/three-view.ts')

  // Clean up any previous instance — covers the rare case of re-activation
  // without an intervening 2D toggle (e.g. mid-flight if the user
  // double-clicks 3D).
  if (threeView) {
    threeView.dispose()
    threeView = null
  }

  container.classList.add('active')
  canvasContainer.classList.add('three-mode')

  threeView = create3DView({
    container,
    projectData: {
      plotWidth: plannerState.plotWidth,
      plotHeight: plannerState.plotHeight,
    },
  })
  renderProject3D({
    scene: threeView.scene,
    boundary: plannerState.boundary,
    sprinklers: plannerState.sprinklers,
    zones: plannerState.zones,
    pipes: plannerState.pipes,
    trees: plannerState.trees,
    valveObjects: plannerState.valveObjects,
    dripLines: plannerState.dripLines,
    weatherStations: plannerState.weatherStations,
  })
}

function activate2D(): void {
  const container = document.getElementById('three-container')
  const canvasContainer = document.getElementById('canvasContainer')

  if (threeView) {
    threeView.dispose()
    threeView = null
  }
  container?.classList.remove('active')
  canvasContainer?.classList.remove('three-mode')
}

function setViewButtonState(target: '2d' | '3d'): void {
  document.querySelectorAll<HTMLButtonElement>('[data-v2-view]').forEach((btn) => {
    const isTarget = btn.dataset.v2View === target
    btn.classList.toggle('on', isTarget)
    btn.setAttribute('aria-pressed', String(isTarget))
  })
}

function bindViewToggle(): void {
  document.querySelectorAll<HTMLButtonElement>('[data-v2-view]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.v2View as '2d' | '3d' | undefined
      if (!target) return
      setViewButtonState(target)
      if (target === '3d') {
        // Skip if WebGL is unavailable — the create3DView call would throw,
        // and the user would be stuck in a half-toggled state.
        if (!featureEnabled('three_d_view')) return
        void activate3D()
      } else {
        activate2D()
      }
    })
  })

  // Canonical 2D/3D switcher lives in the context-bar (`r-2d` / `r-3d`
  // segmented pair, both carrying `data-v2-view`). The duplicate rail
  // single-button (`#btnToggle3D`) was removed in #1320.
}

/* Project-name editing + dropdown switcher in the title-bar (#1328).
 *
 * Three responsibilities:
 *   1. Display the current project's name in the input.
 *   2. Persist edits via PATCH /api/v1/projects/:id on blur or Enter.
 *   3. List recent projects in the dropdown — clicking one navigates
 *      to `/?project=<id>` so the engine reloads onto that project.
 *      "+ New project" creates a fresh empty project and switches to it.
 */
type ProjectSummary = { id: number; name: string; updated_at?: string }

async function fetchRecentProjects(): Promise<ProjectSummary[]> {
  try {
    const resp = await authenticatedFetch(`${api_v1_projects_path()}?sort=updated&per_page=10`)
    if (!resp.ok) return []
    const body = await resp.json()
    const projects = body.projects || []
    return projects.map((p: ProjectSummary) => ({ id: p.id, name: p.name, updated_at: p.updated_at }))
  } catch {
    return []
  }
}

async function persistProjectName(projectId: number, name: string): Promise<boolean> {
  try {
    const resp = await authenticatedFetch(api_v1_project_path(projectId), {
      method: 'PATCH',
      body: { name },
    })
    return resp.ok
  } catch {
    return false
  }
}

function bindProjectSwitcher(): void {
  const input = document.querySelector<HTMLInputElement>('[data-project-name-input]')
  const toggle = document.querySelector<HTMLButtonElement>('[data-project-switcher-toggle]')
  const menu = document.querySelector<HTMLUListElement>('[data-project-switcher-menu]')
  if (!input || !toggle || !menu) return

  // Anonymous users cannot CRUD projects — the API requires JWT auth.
  // Without this guard the input + chevron silently no-op (persist() early-
  // returns on null currentProjectId, and fetchRecentProjects 401s) leaving
  // users to wonder why renaming and switching do nothing. Surface a
  // sign-in prompt instead.
  if (!isAuthenticated()) {
    input.readOnly = true
    input.value = ''
    input.placeholder = t('plannerBp.projectSignInPrompt')
    // The chrome mounts before the engine loads the locale, and initI18n()
    // then re-applies the markup's own `[placeholder]` binding ("Untitled
    // project") — which replaced this prompt before a guest ever saw it.
    // Point that binding at the prompt instead.
    input.dataset.i18n = (input.dataset.i18n ?? '')
      .split(';')
      .filter((binding) => binding.trim() && !binding.trim().startsWith('[placeholder]'))
      .concat('[placeholder]plannerBp.projectSignInPrompt')
      .join(';')
    input.classList.add('pv2-project-name-input--guest')
    toggle.hidden = true
    menu.hidden = true
    return
  }

  // Hydrate input from the project the engine loaded, which it does
  // asynchronously; poll briefly until it lands. The name is read from the
  // engine's copy: fetching the project again downloaded all of its data a
  // second time, and once more every second while that request was pending.
  const hydrate = () => {
    if (input.value || !getCurrentProjectId()) return
    input.value = getCurrentProjectName() || ''
  }
  hydrate()
  // Re-hydrate on every short interval until the input has a value or
  // the page navigates (max ~5s).
  let attempts = 0
  const hydrateTimer = setInterval(() => {
    attempts += 1
    if (attempts >= 5 || input.value) {
      clearInterval(hydrateTimer)
      return
    }
    hydrate()
  }, 1000)

  // Persist name change on blur / Enter.
  const persist = async () => {
    const id = getCurrentProjectId()
    if (!id) return
    const trimmed = input.value.trim()
    if (!trimmed) return
    const ok = await persistProjectName(id, trimmed)
    showToast(
      ok ? t('plannerBp.projectNameSaved') : t('plannerBp.projectNameSaveFailed'),
      ok ? 'success' : 'error',
    )
  }
  input.addEventListener('blur', persist)
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      // Call persist() directly — `input.blur()` does not reliably fire
      // the blur event in jsdom, and even in browsers it's redundant
      // since the user typically tabs away or clicks elsewhere right
      // after pressing Enter.
      persist()
      input.blur()
    }
  })

  // Dropdown — populate on first open, then close on outside click.
  const closeMenu = () => {
    menu.hidden = true
    toggle.setAttribute('aria-expanded', 'false')
  }
  const openMenu = async () => {
    const projects = await fetchRecentProjects()
    const currentId = getCurrentProjectId()
    // Clear existing children — replaceChildren is the modern, secure
    // alternative to innerHTML='' (no XSS surface).
    menu.replaceChildren()
    projects.forEach((p) => {
      const li = document.createElement('li')
      li.setAttribute('role', 'option')
      li.setAttribute('aria-selected', String(p.id === currentId))
      li.tabIndex = -1
      li.dataset.projectId = String(p.id)
      li.textContent = p.name || `#${p.id}`
      if (p.id === currentId) li.classList.add('on')
      li.addEventListener('click', () => {
        if (p.id === currentId) {
          closeMenu()
          return
        }
        // Hard-navigate so engine + autosave + state initialize cleanly
        // for the new project. URL keeps the locale segment.
        const url = new URL(window.location.href)
        url.searchParams.set('project', String(p.id))
        window.location.assign(url.toString())
      })
      menu.appendChild(li)
    })

    // "+ New project" item — POSTs an empty draft, switches to it.
    const newLi = document.createElement('li')
    newLi.setAttribute('role', 'option')
    newLi.setAttribute('aria-selected', 'false')
    newLi.tabIndex = -1
    newLi.classList.add('new')
    newLi.textContent = t('plannerBp.projectNewLabel')
    newLi.addEventListener('click', async () => {
      try {
        const resp = await authenticatedFetch(api_v1_projects_path(), {
          method: 'POST',
          body: { name: t('plannerBp.projectUntitled'), data: {} },
        })
        if (!resp.ok) {
          showToast(t('plannerBp.projectCreateFailed'), 'error')
          return
        }
        const body = await resp.json()
        trackProjectCreated()
        const url = new URL(window.location.href)
        url.searchParams.set('project', String(body.id))
        window.location.assign(url.toString())
      } catch {
        showToast(t('plannerBp.projectCreateFailed'), 'error')
      }
    })
    menu.appendChild(newLi)

    menu.hidden = false
    toggle.setAttribute('aria-expanded', 'true')
    ;(menu.querySelector<HTMLElement>('li.on') ?? menu.querySelector<HTMLElement>('li'))?.focus()
  }
  toggle.addEventListener('click', () => {
    if (menu.hidden) openMenu()
    else closeMenu()
  })

  // The options are <li>s, so nothing in the open list could take focus and
  // the switcher opened for a keyboard user only to be stuck there. Focus now
  // moves in on open (above) and these keys work the list; handled keys stop
  // here so the planner's own Escape/Space do not fire behind the menu.
  menu.addEventListener('keydown', (e) => {
    const items = Array.from(menu.querySelectorAll<HTMLElement>('li[role="option"]'))
    const at = items.indexOf(document.activeElement as HTMLElement)
    const moveTo = (i: number) => items[(i + items.length) % items.length]?.focus()
    switch (e.key) {
      case 'ArrowDown': moveTo(at + 1); break
      case 'ArrowUp': moveTo(at - 1); break
      case 'Home': moveTo(0); break
      case 'End': moveTo(items.length - 1); break
      case 'Enter': case ' ': items[at]?.click(); break
      case 'Escape': closeMenu(); toggle.focus(); break
      case 'Tab': closeMenu(); return
      default: return
    }
    e.preventDefault()
    e.stopPropagation()
  })

  // Outside click closes the menu.
  document.addEventListener('click', (e) => {
    if (menu.hidden) return
    const target = e.target as Node
    if (menu.contains(target) || toggle.contains(target)) return
    closeMenu()
  })
}
